import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { Snackbar, Alert, Button } from '@mui/material';

interface User {
  username: string;
  email: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
  getAuthToken: () => string | null; // Returns Shipium credentials
  refreshAccessToken: () => Promise<boolean>; // Refreshes the access token
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);

  // Inactivity timeout: 15 minutes (in milliseconds)
  const INACTIVITY_TIMEOUT = 15 * 60 * 1000;
  // Show warning 2 minutes before timeout
  const WARNING_TIME = 13 * 60 * 1000;

  // Logout function (defined early so it can be used in useEffect)
  const logout = useCallback(() => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('refreshToken');
    setUser(null);
    setShowTimeoutWarning(false);
  }, []);

  // Refresh access token using refresh token
  const refreshAccessToken = useCallback(async (): Promise<boolean> => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      console.warn('[Auth] No refresh token available');
      return false;
    }

    try {
      const apiBaseUrl = process.env.NODE_ENV === 'production' 
        ? '' 
        : 'http://localhost:3001';
      
      console.log('[Auth] Attempting to refresh access token...');
      
      const response = await fetch(`${apiBaseUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        // Try to get error message from response
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          // If JSON parsing fails, use status text
        }
        
        console.error('[Auth] Token refresh failed:', errorMessage);
        
        // If refresh token is invalid (401), clear all tokens
        if (response.status === 401 || response.status === 403) {
          console.warn('[Auth] Refresh token is invalid, clearing session');
          logout();
        }
        
        return false;
      }

      const refreshData = await response.json();
      const newAccessToken = refreshData.accessToken;
      
      if (!newAccessToken) {
        console.error('[Auth] No access token in refresh response:', refreshData);
        return false;
      }

      // Update stored access token
      const newAuthHeader = `Bearer ${newAccessToken}`;
      localStorage.setItem('authToken', newAuthHeader);
      
      // Update refresh token if a new one is provided
      if (refreshData.refreshToken) {
        localStorage.setItem('refreshToken', refreshData.refreshToken);
      } else {
        // If no new refresh token, keep the old one
        console.warn('[Auth] No new refresh token provided, keeping existing one');
      }

      console.log('[Auth] Token refreshed successfully');
      return true;
    } catch (error: any) {
      console.error('[Auth] Error refreshing token:', error);
      console.error('[Auth] Error details:', {
        message: error.message,
        stack: error.stack,
      });
      
      // Network errors or other failures - don't clear session, just return false
      // The user might still be able to use the app if the token is still valid
      return false;
    }
  }, [logout]);

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('authToken');
      const userData = localStorage.getItem('userData');
      
      if (token && userData) {
        try {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
        } catch (error) {
      // Invalid user data, clear it
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
      localStorage.removeItem('refreshToken');
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  // Inactivity timeout handler
  useEffect(() => {
    if (!user) {
      // User is not logged in, no need for timeout
      return;
    }

    let inactivityTimer: NodeJS.Timeout;
    let warningTimer: NodeJS.Timeout;

    const resetTimer = () => {
      // Clear existing timers
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }
      if (warningTimer) {
        clearTimeout(warningTimer);
      }

      // Hide warning if user is active
      setShowTimeoutWarning(false);

      // Set warning timer (13 minutes - shows warning 2 minutes before logout)
      warningTimer = setTimeout(() => {
        console.log('Showing inactivity warning');
        setShowTimeoutWarning(true);
      }, WARNING_TIME);

      // Set logout timer (15 minutes)
      inactivityTimer = setTimeout(() => {
        console.log('Session timed out due to inactivity (15 minutes)');
        logout();
      }, INACTIVITY_TIMEOUT);
    };

    // Events that indicate user activity
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];

    // Reset timer on any user activity
    activityEvents.forEach((event) => {
      window.addEventListener(event, resetTimer);
    });

    // Initialize the timer
    resetTimer();

    // Cleanup
    return () => {
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }
      if (warningTimer) {
        clearTimeout(warningTimer);
      }
      activityEvents.forEach((event) => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, [user, logout, WARNING_TIME, INACTIVITY_TIMEOUT]);

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      setLoading(true);
      
      console.log('Authenticating with Shipium SSO for:', username);
      console.log('SSO endpoint:', '/auth/login (via backend proxy)');
      
      const requestBody = {
        email: username,
        password: password
      };
      console.log('Request body (password hidden):', { email: username, password: '***' });
      
      // Make SSO login request via our backend (to avoid CORS)
      // Use the API server URL (port 3001) for API calls
      const apiBaseUrl = process.env.NODE_ENV === 'production' 
        ? '' // In production, API and frontend are served from the same origin
        : 'http://localhost:3001'; // In development, API is on port 3001
      
      const response = await fetch(`${apiBaseUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });
      
      console.log('SSO response status:', response.status);
      console.log('SSO response statusText:', response.statusText);
      console.log('SSO response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        let errorData;
        const contentType = response.headers.get('content-type');
        console.log('Error response content-type:', contentType);
        
        if (contentType && contentType.includes('application/json')) {
          errorData = await response.json();
        } else {
          const errorText = await response.text();
          console.log('Error response text:', errorText);
          errorData = { message: errorText || 'Authentication failed' };
        }
        
        console.error('Shipium SSO failed:', response.status, response.statusText);
        console.error('Error details:', errorData);
        return false;
      }
      
      const loginData = await response.json();
      console.log('SSO login successful');
      console.log('Login response data:', loginData);
      
      // The SSO response should contain authentication tokens
      // Extract accessToken from the Shipium SSO response
      const accessToken = loginData.accessToken;
      
      if (!accessToken) {
        console.error('No accessToken received from SSO login response');
        console.error('Response data:', loginData);
        return false;
      }
      
      // Store the access token as Authorization header
      // The token format from Shipium is typically a bearer token or basic auth token
      // Based on Shipium API, we should use it as: Bearer <token> or Basic <token>
      // Since the user mentioned the token should be used to access Shipium endpoints,
      // and the backend expects Authorization header, we'll use Bearer format
      const authHeader = `Bearer ${accessToken}`;
      console.log('Storing Shipium access token');
      
      // Also store user data from the response
      const userInfo = loginData.user || {};
      const userData: User = {
        username: userInfo.email || username,
        email: userInfo.email || username
      };
      
      // Store Shipium credentials for API calls
      localStorage.setItem('authToken', authHeader);
      localStorage.setItem('userData', JSON.stringify(userData));
      
      // Store refresh token if available
      const refreshToken = loginData.refreshToken;
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
        console.log('Stored refresh token for token renewal');
      }
      
      setUser(userData);
      return true;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Get Shipium credentials from localStorage
  const getAuthToken = (): string | null => {
    return localStorage.getItem('authToken');
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    login,
    logout,
    loading,
    getAuthToken,
    refreshAccessToken
  };

  const handleStayLoggedIn = () => {
    setShowTimeoutWarning(false);
    // Trigger a synthetic event to reset the timer
    window.dispatchEvent(new Event('click'));
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <Snackbar
        open={showTimeoutWarning}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="warning"
          sx={{ width: '100%', alignItems: 'center' }}
          action={
            <Button color="inherit" size="small" onClick={handleStayLoggedIn}>
              Stay Logged In
            </Button>
          }
        >
          You will be logged out in 2 minutes due to inactivity.
        </Alert>
      </Snackbar>
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
